/**
 * IntegrationListingFilters
 *
 * Renders the SearchAndFilters bar for the IntegrationListing page.
 * Mirrors the Filters.tsx pattern used in EPDashboard/EPList.
 *
 * On every change the filter values are written to the central Jotai atom
 * and pagination is reset to page 1 so the grid always shows the first page
 * of the freshly-filtered results.
 */

import SearchAndFilters from 'components/SearchAndFilters';
import { FilterConfig } from 'components/SearchAndFilters/FilterTypes';
import Tooltip from 'components/Tooltip';
import { useSetAtom, useAtom } from 'jotai';
import React, { Suspense } from 'react';
import { Row, Col, Button } from 'reactstrap';

import {
  filterValuesAtom,
  isIntegrationCardsVisibleAtom,
  useOnFilterChangeResetRelatedAtoms,
} from '../../state';
import '../../styles.css';

interface IntegrationListingFiltersProps {
  filtersConfig: FilterConfig;
}

/**
 * IntegrationListingFilters — thin wrapper around the shared SearchAndFilters
 * component. Writing to `filterValuesAtom` triggers the derived query-param
 * atom, which in turn triggers a grid re-fetch.
 */
const IntegrationListingFilters: React.FC<IntegrationListingFiltersProps> = ({
  filtersConfig,
}) => {
  const setFilterValues = useSetAtom(filterValuesAtom);
  const resetOnFilterChange = useOnFilterChangeResetRelatedAtoms();
  const [isIntegrationCardsVisible, setIsIntegrationCardsVisible] = useAtom(
    isIntegrationCardsVisibleAtom,
  );
  const integrationToggleTooltip = isIntegrationCardsVisible
    ? 'Collapse Integration Cards'
    : 'Show Integration Cards';

  const handleFilterChange = () => {
    // Read the latest active values from the config object and persist them.
    setFilterValues({ ...filtersConfig.getActiveFilterValues() });
    // Reset row-selection and pagination whenever filters change.
    resetOnFilterChange();
  };

  return (
    <Suspense fallback={<></>}>
      <Row className="text-left align-items-center">
        <Col xs="12" md>
          <SearchAndFilters
            config={filtersConfig}
            onChange={handleFilterChange}
          />
        </Col>

        {/* Separator */}
        <Col xs="auto" className="d-none d-md-block px-1">
          <span className="integration-listing-filter-separator">|</span>
        </Col>

        <Col xs="auto" className="text-right">
          <Tooltip tooltip={integrationToggleTooltip}>
            <Button
              color="primary"
              onClick={() =>
                setIsIntegrationCardsVisible(!isIntegrationCardsVisible)
              }
              className="integration-listing-filter-toggle px-3 d-flex align-items-center justify-content-center"
              aria-label={integrationToggleTooltip}
            >
              {/* <span>New Integration</span> */}
              <i
                className={`fa fa-${
                  isIntegrationCardsVisible ? 'chevron-up' : 'plus'
                }`}
              />
            </Button>
          </Tooltip>
        </Col>
      </Row>
    </Suspense>
  );
};

export default IntegrationListingFilters;
